import { useEffect, useRef, useState } from "react";
import { Shield, Cloud, Smartphone, Server } from "lucide-react";

interface Milestone {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const milestones: Milestone[] = [
  {
    icon: <Server className="w-8 h-8" />,
    title: "Infraestructura",
    description: "Servidores, redes y sistemas adaptados a cada negocio",
  },
  {
    icon: <Smartphone className="w-8 h-8" />,
    title: "Movilidad Empresarial",
    description: "Smartphones y tablets integrados en el trabajo",
  },
  {
    icon: <Shield className="w-8 h-8" />,
    title: "Ciberseguridad",
    description: "Protección de activos digitales como prioridad",
  },
  {
    icon: <Cloud className="w-8 h-8" />,
    title: "Cloud Computing",
    description: "AWS, Google Cloud, Azure para cada necesidad",
  },
];

const CrisisYReinvencionSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={sectionRef} className="max-w-5xl mx-auto">
      <div className="text-center mb-16">
        <p className="text-sm font-light tracking-[0.2em] text-muted-foreground uppercase mb-6">
          Lo que hemos atravesado
        </p>
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-light text-foreground leading-tight mb-6">
          La crisis que nos hizo<br />
          <span className="text-muted-foreground">más fuertes.</span>
        </h2>
      </div>

      {/* Las Crisis */}
      <div 
        className={`max-w-3xl mx-auto mb-20 transition-all duration-700 ease-out ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        <div className="grid md:grid-cols-2 gap-12">
          <div className="text-center">
            <div className="text-5xl md:text-6xl font-bold text-muted-foreground/20 mb-4">
              2008
            </div>
            <p className="text-xl md:text-2xl font-light text-foreground mb-2">
              Crisis financiera global
            </p>
            <p className="text-lg md:text-xl font-light text-muted-foreground">
              Perdimos el 20% de clientes. Respondimos certificándonos con Microsoft y ampliando servicios.
            </p>
          </div>
          <div className="text-center">
            <div className="text-5xl md:text-6xl font-bold text-muted-foreground/20 mb-4">
              2020
            </div>
            <p className="text-xl md:text-2xl font-light text-foreground mb-2">
              Pandemia COVID-19
            </p>
            <p className="text-lg md:text-xl font-light text-muted-foreground">
              Aceleramos la digitalización de nuestros clientes. Cloud y trabajo remoto pasaron de opción a necesidad.
            </p>
          </div>
        </div>
      </div>

      {/* Hitos de Evolución */}
      <div 
        className={`transition-all duration-700 ease-out delay-300 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        <h3 className="text-2xl md:text-3xl lg:text-4xl font-light text-foreground text-center mb-12">
          Cómo evolucionamos
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {milestones.map((milestone, index) => (
            <div 
              key={milestone.title}
              className="text-center p-6"
              style={{ transitionDelay: `${(index + 3) * 100}ms` }}
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4 text-foreground">
                {milestone.icon}
              </div>
              <h4 className="text-lg md:text-xl font-light text-foreground mb-2">
                {milestone.title}
              </h4>
              <p className="text-base md:text-lg font-light text-muted-foreground">
                {milestone.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CrisisYReinvencionSection;
